import { useMemo } from 'react';

import { PurchaseError } from 'models/errors/purchase-error';
import { IStorePackageVariation } from 'models/store/i-store-package-variation.interface';
import { IStorePackage } from 'models/store/i-store-package.interface';

import { CheckoutProgress } from 'enums/checkout-progress.enum';

import { WEB3 } from 'constants/web3.constant';

import { adaptStateHash } from 'services/market.service';

import { IEvents } from 'hooks/events/useEventsParams';

import { parseDecimal } from 'utils/parser.utils';
import { selectRandomUnusedOffer } from 'utils/select-unused-offer.utils';
import { validateTokenAmount } from 'utils/validation.utils';

import { useCurrencyWithBalance } from './useCurrency';
import { useEnv } from './useEnv';
import { usePurchaseCore } from './usePurchaseCore';

type UsePackagePurchaseProps = {
  packageDetails: IStorePackage;
  events?: IEvents;
  selectedOffer: IStorePackageVariation;
};

export const usePackagePurchase = ({ packageDetails, events, selectedOffer }: UsePackagePurchaseProps) => {
  const core = usePurchaseCore();
  const currency = useCurrencyWithBalance(packageDetails.currency.id);
  const { contractAddress } = useEnv();

  const packageId = packageDetails.id.toString();
  const buyParams = {
    packageId: packageId,
    packageName: packageDetails.name,
    fnftsAmount: packageDetails.total_shares,
    nftIds: packageDetails.items.map((item) => item.asset_token_id.toString()).join(','),
    fnftsAmounts: packageDetails.items
      .map((item) => (item.amount * selectedOffer.amount_multiplier).toString())
      .join(','),
    amountRmv: currency ? parseFloat(parseDecimal(selectedOffer.price, currency.decimal)) : undefined,
  };

  const isPurchaseFinished = useMemo(
    () => !core.isAddFundsOpen && core.progressStep === CheckoutProgress.end,
    [core.isAddFundsOpen, core.progressStep]
  );
  const isCheckoutInProgress = useMemo(
    () => !core.isAddFundsOpen && core.progressStep === CheckoutProgress.start,
    [core.isAddFundsOpen, core.progressStep]
  );
  const isPurchaseInProgress = useMemo(
    () => !core.isAddFundsOpen && core.progressStep === CheckoutProgress.inProgress,
    [core.isAddFundsOpen, core.progressStep]
  );

  const purchase = async () => {
    core.setConfirmationPending(true);

    try {
      // Validate network
      await core.validateNetwork(packageDetails.currency.network);

      // Validate seller address
      core.validateSellerAddress(packageDetails.seller.address);

      // Ensure currency is loaded
      if (!currency) {
        throw PurchaseError.unknown(new Error('Currency not loaded'));
      }

      // Check sufficient balance
      const hasSufficientBalance = core.checkSufficientBalance(currency, selectedOffer.price, buyParams);
      if (!hasSufficientBalance) {
        core.setConfirmationPending(false);
        return;
      }

      // Ensure currency permissions
      await core.ensureCurrencyPermissions(currency, selectedOffer.price);

      // Randomly select an unused offer from available offers
      const offerToUse = await selectRandomUnusedOffer(selectedOffer.available_trade_offers);

      // If no available offer found, throw error
      if (!offerToUse) {
        throw PurchaseError.noAvailableOffers('No available offers found. All signatures have been used.');
      }

      // Validate item amounts don't overflow
      packageDetails.items.forEach((item) => {
        const totalAmount = item.amount * selectedOffer.amount_multiplier;
        validateTokenAmount(totalAmount);
      });

      // Execute purchase
      await core.lgoWeb3.purchesAsset({
        to: packageDetails.seller.address,
        signature: offerToUse.signature,
        tradeRequest: {
          nonce: offerToUse.nonce.toString(),
          expirationTime: offerToUse.expiration_time.toString(),
          tradeTokenAddress: currency.address,
          itemsTokenAddress: contractAddress,
          items: packageDetails.items.map((item) => ({
            tokenId: item.asset_token_id.toString(),
            amount: item.amount * selectedOffer.amount_multiplier,
            stateHash: adaptStateHash('0x0'),
          })),
          offerValue: selectedOffer.price.toString(),
          to: WEB3.nullAddress,
        },
        walletAddress: core.accountAddress,
        onTransactionHash: (hash: string) => core.handleTransactionHash(hash, buyParams, events),
        offerAccepted: () => core.handlePurchaseSuccess(buyParams, events),
        offerCanceled: () => core.handlePurchaseCanceled(buyParams, events),
      });
    } catch (error) {
      core.handlePurchaseError(error, buyParams, events);
    }
  };

  return {
    confirmationPending: core.confirmationPending,
    fundsRemaining: core.fundsRemaining,
    fundsRemainingCurrency: core.fundsRemainingCurrency,
    isAddFundsOpen: core.isAddFundsOpen,
    isPurchaseFinished,
    isCheckoutInProgress,
    isPurchaseInProgress,
    progressStep: core.progressStep,
    purchase,
    setIsAddFundsOpen: core.setIsAddFundsOpen,
    transactionHash: core.transactionHash,
    selectedOffer,
  };
};
